<template>
  <div>
    <!-- Overlay -->
    <transition name="fade">
      <div v-if="isOpen" class="chat-panel-overlay" @click="close"></div>
    </transition>

    <!-- Slide-out Panel -->
    <transition name="slide">
      <div v-if="isOpen" class="chat-panel">
        <!-- Header -->
        <div class="chat-panel__header">
          <h2 class="chat-panel__title">
            <i class="mdi mdi-chat" />
            {{ $t("message.chat.panel_title") || "Chat" }}
          </h2>
          <button @click="close" class="chat-panel__close">&times;</button>
        </div>

        <!-- Stats bar -->
        <div class="chat-panel__stats">
          <span>
            <i class="mdi mdi-clock-outline" />
            {{ queue.length }} {{ $t("message.chat.waiting") || "waiting" }}
          </span>
          <span>
            <i class="mdi mdi-account-check" />
            {{ activeConversations.length }}
            {{ $t("message.chat.active") || "active" }}
          </span>
          <span v-if="unprocessedSubmissions.length > 0">
            <i class="mdi mdi-file-document-outline" />
            {{ unprocessedSubmissions.length }}
            {{ $t("message.forms_page.new_responses") || "new responses" }}
          </span>
        </div>

        <!-- List -->
        <div class="chat-panel__list">
          <!-- Queue section -->
          <div v-if="queue.length > 0">
            <div class="chat-panel__section-label">
              {{ $t("message.chat.incoming") || "Incoming" }}
            </div>
            <div
              v-for="conv in queue"
              :key="conv.conversationId"
              class="chat-panel__item chat-panel__item--waiting"
              @click="openConversation(conv.conversationId)"
            >
              <div class="chat-panel__item-icon">
                <i class="mdi mdi-account" />
              </div>
              <div class="chat-panel__item-body">
                <div class="chat-panel__item-email">
                  {{
                    conv.clientEmail ||
                      conv.client_email ||
                      "Chat #" + (conv.conversationId || conv.id)
                  }}
                </div>
                <div class="chat-panel__item-time">
                  {{ formatTime(conv.createdAt) }}
                </div>
              </div>
              <span class="chat-panel__badge chat-panel__badge--waiting">
                {{ $t("message.chat.waiting") || "waiting" }}
              </span>
            </div>
          </div>

          <!-- My conversations section -->
          <div v-if="activeConversations.length > 0">
            <div class="chat-panel__section-label">
              {{ $t("message.chat.my_conversations") || "My Conversations" }}
            </div>
            <div
              v-for="conv in activeConversations"
              :key="conv.id"
              class="chat-panel__item"
              :class="{
                'chat-panel__item--closed': conv.status === 'closed'
              }"
              @click="openConversation(conv.id)"
            >
              <div class="chat-panel__item-icon">
                <i class="mdi mdi-account-check" />
              </div>
              <div class="chat-panel__item-body">
                <div class="chat-panel__item-email">
                  {{
                    conv.clientEmail ||
                      conv.client_email ||
                      "Chat #" + (conv.conversationId || conv.id)
                  }}
                </div>
                <div class="chat-panel__item-time">
                  {{ formatTime(conv.createdAt) }}
                </div>
              </div>
              <span
                class="chat-panel__badge"
                :class="
                  conv.status === 'closed'
                    ? 'chat-panel__badge--closed'
                    : 'chat-panel__badge--active'
                "
              >
                {{ conv.status }}
              </span>
            </div>
          </div>

          <!-- Form submissions section -->
          <div v-if="unprocessedSubmissions.length > 0">
            <div class="chat-panel__section-label">
              {{ $t("message.forms_page.new_responses") || "Form Responses" }}
            </div>
            <div
              v-for="submission in unprocessedSubmissions"
              :key="'form-' + submission.id"
              class="chat-panel__item chat-panel__item--form"
              @click="openSubmission(submission)"
            >
              <div class="chat-panel__item-icon chat-panel__item-icon--form">
                <i class="mdi mdi-file-document-outline" />
              </div>
              <div class="chat-panel__item-body">
                <div class="chat-panel__item-email">
                  {{
                    submission.form_name ||
                      submission.formName ||
                      "Form #" + submission.id
                  }}
                </div>
                <div class="chat-panel__item-time">
                  {{
                    formatTime(submission.createdAt || submission.created_at)
                  }}
                  <span v-if="submission.sellerName">
                    · {{ submission.sellerName }}</span
                  >
                </div>
              </div>
              <span class="chat-panel__badge chat-panel__badge--form">
                {{ $t("message.forms_page.new_badge") || "new" }}
              </span>
            </div>
          </div>

          <!-- Empty state -->
          <div
            v-if="
              queue.length === 0 &&
                activeConversations.length === 0 &&
                unprocessedSubmissions.length === 0
            "
            class="chat-panel__empty"
          >
            <i class="mdi mdi-chat-outline" />
            <span>
              {{ $t("message.chat.no_conversations") || "No conversations" }}
            </span>
          </div>
        </div>

        <!-- Footer -->
        <div class="chat-panel__footer">
          <button class="chat-panel__view-all" @click="goToChat">
            {{ $t("message.chat.view_all") || "View all chats" }}
            <i class="mdi mdi-arrow-right" />
          </button>
        </div>
      </div>
    </transition>
  </div>
</template>

<script>
export default {
  name: "ChatQueuePanel",
  props: {
    isOpen: {
      type: Boolean,
      default: false
    }
  },
  computed: {
    queue() {
      return this.$store.state.chat.queue;
    },
    activeConversations() {
      return this.$store.state.chat.myConversations;
    },
    unprocessedSubmissions() {
      return this.$store.state.forms.unprocessedSubmissions;
    }
  },
  methods: {
    close() {
      this.$emit("close");
    },
    openConversation(conversationId) {
      this.$store.commit("chat/SELECT_CONVERSATION", conversationId);
      this.goToChat(conversationId);
    },
    openSubmission(submission) {
      this.$router
        .push({
          name: "FormSubmissionDetailsPage",
          params: {
            storeCode: this.$route.params.storeCode,
            departmentCode: this.$route.params.departmentCode,
            submissionId: submission.id
          }
        })
        .catch(() => {});
      this.close();
    },
    goToChat(chatId) {
      const route = {
        name: "ChatPage",
        params: {
          storeCode: this.$route.params.storeCode,
          departmentCode: this.$route.params.departmentCode
        }
      };
      if (chatId) route.query = { id: String(chatId) };
      this.$router.push(route).catch(() => {});
      this.close();
    },
    formatTime(dateStr) {
      if (!dateStr) return "";
      const date = new Date(dateStr.replace(" ", "T"));
      if (isNaN(date.getTime())) return "";

      const now = new Date();
      const diffMs = now - date;
      const diffMins = Math.floor(diffMs / 60000);
      const diffHours = Math.floor(diffMs / 3600000);

      if (diffMins < 1) return "Just now";
      if (diffMins < 60) return `${diffMins}m ago`;
      if (diffHours < 24) return `${diffHours}h ago`;
      return date.toLocaleDateString("en-US", {
        month: "short",
        day: "numeric",
        hour: "2-digit",
        minute: "2-digit"
      });
    }
  }
};
</script>

<style lang="scss" scoped>
.chat-panel-overlay {
  position: fixed;
  inset: 0;
  background-color: rgba(0, 0, 0, 0.3);
  z-index: 40;
}

.chat-panel {
  position: fixed;
  right: 0;
  top: 0;
  /* dvh: en iOS las barras del navegador se comen el 100% del viewport
     y tapaban el footer del panel. */
  height: 100vh;
  height: 100dvh;
  width: 100%;
  max-width: 380px;
  background: white;
  box-shadow: -4px 0 20px rgba(0, 0, 0, 0.15);
  z-index: 50;
  display: flex;
  flex-direction: column;

  &__header {
    background: #2d2d2d;
    color: white;
    padding: 16px 20px;
    display: flex;
    align-items: center;
    justify-content: space-between;
  }

  &__title {
    font-size: 16px;
    font-weight: 600;
    display: flex;
    align-items: center;
    gap: 8px;
    margin: 0;

    i {
      font-size: 20px;
    }
  }

  &__close {
    background: none;
    border: none;
    color: white;
    font-size: 24px;
    line-height: 1;
    cursor: pointer;
    padding: 0 4px;
    opacity: 0.8;

    &:hover {
      opacity: 1;
    }
  }

  &__stats {
    padding: 10px 20px;
    background: #f8f8f8;
    border-bottom: 1px solid #eee;
    display: flex;
    gap: 20px;
    font-size: 13px;
    color: #555;

    span {
      display: flex;
      align-items: center;
      gap: 5px;

      i {
        font-size: 16px;
      }
    }
  }

  &__list {
    flex: 1;
    overflow-y: auto;
  }

  &__section-label {
    padding: 8px 20px 4px;
    font-size: 11px;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.5px;
    color: #888;
    background: #f8f8f8;
    border-bottom: 1px solid #eee;
  }

  &__item {
    display: flex;
    align-items: center;
    gap: 12px;
    padding: 12px 20px;
    border-bottom: 1px solid #f0f0f0;
    cursor: pointer;
    transition: background 0.15s;

    &:hover {
      background: #f5f5f5;
    }

    &--waiting {
      background: #fff8e1;
      border-left: 3px solid #f59e0b;

      &:hover {
        background: #fef3c7;
      }
    }

    &--closed {
      opacity: 0.6;
    }

    &--form {
      background: #e8f5e9;
      border-left: 3px solid #4caf50;

      &:hover {
        background: #c8e6c9;
      }
    }
  }

  &__item-icon--form {
    background: #c8e6c9;

    i {
      color: #2e7d32;
    }
  }

  &__item-icon {
    width: 36px;
    height: 36px;
    border-radius: 50%;
    background: #e5e7eb;
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;

    i {
      font-size: 18px;
      color: #555;
    }
  }

  &__item-body {
    flex: 1;
    min-width: 0;
  }

  &__item-email {
    font-size: 13px;
    font-weight: 500;
    color: #222;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  &__item-time {
    font-size: 11px;
    color: #888;
    margin-top: 2px;
  }

  &__badge {
    font-size: 10px;
    font-weight: 700;
    padding: 2px 8px;
    border-radius: 10px;
    text-transform: uppercase;
    white-space: nowrap;
    flex-shrink: 0;

    &--waiting {
      background: #fef3c7;
      color: #d97706;
    }

    &--active {
      background: #d1fae5;
      color: #059669;
    }

    &--closed {
      background: #f3f4f6;
      color: #6b7280;
    }

    &--form {
      background: #e8f5e9;
      color: #2e7d32;
    }
  }

  &__empty {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding: 48px 20px;
    color: #aaa;
    gap: 12px;

    i {
      font-size: 48px;
    }

    span {
      font-size: 14px;
    }
  }

  &__footer {
    padding: 16px 20px;
    border-top: 1px solid #eee;
  }

  &__view-all {
    width: 100%;
    padding: 10px;
    background: #2d2d2d;
    color: white;
    border: none;
    border-radius: 6px;
    font-size: 13px;
    font-weight: 600;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
    transition: background 0.2s;

    &:hover {
      background: #444;
    }
  }
}

.slide-enter-active,
.slide-leave-active {
  transition: transform 0.3s ease;
}
.slide-enter,
.slide-leave-to {
  transform: translateX(100%);
}

.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.3s ease;
}
.fade-enter,
.fade-leave-to {
  opacity: 0;
}
</style>
